/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import {
  FloatingPortal,
  Placement,
  autoUpdate,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'
import classNames from 'classnames'
import { useEffect, useState } from 'react'

type Props = {
  button: React.ReactNode
  children: React.ReactNode
  placement?: Placement
  className?: string
  closeOnClick?: boolean
  offsetPosition?: number
  shiftPosition?: number
  getOpenState?: (isOpen: boolean) => void
}

export function Dropdown({
  button,
  children,
  placement = `right`,
  className,
  closeOnClick = false,
  offsetPosition = 8,
  shiftPosition = 0,
  getOpenState,
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  let floatingData = useFloating({
    placement: placement,
    middleware: [offset(offsetPosition), shift({ padding: shiftPosition })],
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
  })

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(floatingData.context),
    useDismiss(floatingData.context),
  ])

  const handleClickInFloatingPortal = () => {
    if (closeOnClick) {
      setIsOpen(false)
    }
  }

  useEffect(() => {
    getOpenState?.(isOpen)
  }, [isOpen])

  return (
    <>
      <div
        x-component-name="dropdown"
        ref={floatingData.refs.setReference}
        className="relative flex items-center"
        {...getReferenceProps()}
      >
        {button}
      </div>
      {isOpen && (
        <FloatingPortal>
          <div
            x-component-name="dropdown-panel"
            ref={floatingData.refs.setFloating}
            className={classNames(`absolute z-50 rounded border border-default bg-surface-raised`, className)}
            {...getFloatingProps()}
            style={{ ...floatingData.floatingStyles }}
            onClick={handleClickInFloatingPortal}
          >
            {children}
          </div>
        </FloatingPortal>
      )}
    </>
  )
}
